import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    EmptyState,
    Label,
    ListBox,
    Separator,
} from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { NO_BRAND_KEY, type NamedOption } from './productFiltersMeta';
import { resolveFilterWords } from './filterLabels';
import type { FilterOptionsState } from './useFilterOptionsState';
import type { UseBrandFilterPickerResult } from './useBrandFilterPicker';
import { BrandSearchField } from './BrandSearchField';
import { FilterOptionsStatus } from './FilterOptionsStatus';
import { FilterPickerFooter } from './FilterPickerFooter';
import { FilterPickerTags } from './FilterPickerTags';

type BrandFilterPickerViewProps = {
    options: FilterOptionsState<NamedOption>;
    picker: UseBrandFilterPickerResult;
};

export const BrandFilterPickerView = ({
    options,
    picker,
}: BrandFilterPickerViewProps) => {
    const { t } = useTranslation('pages');
    const noBrand = t('products.components.productTable.filters.noBrand');
    const tags = picker.tags.map(({ key, label }) => ({
        key,
        label: resolveFilterWords(label, (key, values) => t(key, values)),
    }));

    return (
        <Autocomplete
            className="w-full gap-2"
            onChange={picker.onSelectionChange}
            placeholder={t(
                'products.components.productTable.filters.brandPlaceholder',
            )}
            selectionMode="multiple"
            value={picker.pickedKeys}
            variant="secondary"
            {...getNodeTestIdProp('product-filters-brand')}
        >
            <Label>
                {t('products.components.productTable.filters.brandLabel')}
            </Label>
            <Autocomplete.Trigger>
                <Autocomplete.Value>
                    {({ defaultChildren }) =>
                        tags.length === 0 ? (
                            defaultChildren
                        ) : (
                            <FilterPickerTags
                                onRemove={picker.onRemove}
                                tags={tags}
                            />
                        )
                    }
                </Autocomplete.Value>
                <Autocomplete.ClearButton
                    aria-label={t(
                        'products.components.productTable.filters.brandClear',
                    )}
                />
                <Autocomplete.Indicator />
            </Autocomplete.Trigger>
            <Autocomplete.Popover>
                <Autocomplete.Filter filter={picker.filter}>
                    <BrandSearchField />
                    <ListBox
                        aria-label={t(
                            'products.components.productTable.filters.brandList',
                        )}
                        renderEmptyState={() =>
                            // Loading or failed says so below instead: an
                            // empty list there isn't "no match".
                            options.status === 'ready' ? (
                                <EmptyState>
                                    {t(
                                        'products.components.productTable.filters.brandNoResults',
                                    )}
                                </EmptyState>
                            ) : null
                        }
                    >
                        <ListBox.Item id={NO_BRAND_KEY} textValue={noBrand}>
                            {noBrand}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        <Separator />
                        {options.items.map((option) => (
                            <ListBox.Item
                                id={option.id}
                                key={option.id}
                                textValue={option.name}
                            >
                                {option.name}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Autocomplete.Filter>
                {options.status === 'ready' ? null : (
                    <FilterOptionsStatus
                        onRetry={options.onRetry}
                        status={options.status}
                        subject="brand"
                    />
                )}
                <FilterPickerFooter
                    count={picker.pickedKeys.length}
                    subject="brand"
                    onClear={picker.onClear}
                />
            </Autocomplete.Popover>
        </Autocomplete>
    );
};
